import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    LogOut,
    Mail,
    Coins,
    ArrowLeft,
    Newspaper,
    Briefcase,
    Plus,
    Pencil,
    Trash2,
    X,
    Save,
    Loader2,
    Users,
    Download,
    BookOpen,
    MessageSquare,
    Star,
    FileText,
    ChevronDown,
    ChevronRight,
    RefreshCw,
    UserCog,
    HandHeart,
    Handshake,
} from "lucide-react";
import { t as copyDefaults } from "../i18n";
import { COPY_SCHEMA, readPath } from "../copySchema";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
    const token = localStorage.getItem("ur_admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminDashboard() {
    const [me, setMe] = useState(null);
    const [stats, setStats] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [donations, setDonations] = useState([]);
    const [news, setNews] = useState([]);
    const [activities, setActivities] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [whyPoints, setWhyPoints] = useState([]);
    const [feedback, setFeedback] = useState([]);
    const [overrides, setOverrides] = useState({});
    const [teamMembers, setTeamMembers] = useState([]);
    const [supportOptions, setSupportOptions] = useState([]);
    const [tab, setTab] = useState("contacts");
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    const fetchAll = async () => {
        try {
            const cfg = { headers: authHeaders(), withCredentials: true };
            const [m, s, c, d, n, a, sub, w, fb, co, tm, so] = await Promise.all([
                axios.get(`${API}/auth/me`, cfg),
                axios.get(`${API}/admin/stats`, cfg),
                axios.get(`${API}/admin/contacts`, cfg),
                axios.get(`${API}/admin/donations`, cfg),
                axios.get(`${API}/admin/news`, cfg),
                axios.get(`${API}/admin/activities`, cfg),
                axios.get(`${API}/admin/subscribers`, cfg),
                axios.get(`${API}/admin/why-sport`, cfg),
                axios.get(`${API}/admin/feedback`, cfg),
                axios.get(`${API}/copy`),
                axios.get(`${API}/admin/team`, cfg),
                axios.get(`${API}/admin/support`, cfg),
            ]);
            setMe(m.data);
            setStats(s.data);
            setContacts(c.data);
            setDonations(d.data);
            setNews(n.data);
            setActivities(a.data);
            setSubscribers(sub.data);
            setWhyPoints(w.data);
            setFeedback(fb.data);
            setOverrides(co.data || {});
            setTeamMembers(tm.data);
            setSupportOptions(so.data);
        } catch (err) {
            nav("/admin/login");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (_e) {
            /* ignore logout errors */
        }
        localStorage.removeItem("ur_admin_token");
        nav("/admin/login");
    };

    const downloadContentExport = async () => {
        try {
            const res = await axios.get(`${API}/admin/content-export.md`, {
                headers: authHeaders(),
                withCredentials: true,
                responseType: "blob",
            });
            const disposition = res.headers["content-disposition"] || "";
            const match = disposition.match(/filename="?([^";]+)"?/);
            const filename = match?.[1] || "urban-rookies-content-export.md";
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/markdown;charset=utf-8" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Content export downloaded");
        } catch (_err) {
            toast.error("Could not download content export");
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-ink text-zinc-400 flex items-center justify-center">Loading…</div>;
    }

    return (
        <div className="min-h-screen bg-ink text-white" data-testid="admin-dashboard">
            <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link to="/" className="text-zinc-400 hover:text-volt flex items-center gap-2 text-xs uppercase tracking-wider">
                        <ArrowLeft size={16} /> Site
                    </Link>
                    <div className="h-6 w-px bg-white/10" />
                    <h1 className="font-display font-black uppercase text-xl">Urban Rookies · Admin</h1>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-xs text-zinc-500 hidden md:inline">{me?.email}</span>
                    <button
                        data-testid="admin-content-export-btn"
                        onClick={downloadContentExport}
                        title="Download current website content as Markdown"
                        className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2"
                    >
                        <Download size={14} /> <span className="hidden sm:inline">Export content</span>
                    </button>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2"
                    >
                        <LogOut size={14} /> Logout
                    </button>
                </div>
            </header>

            <main className="px-6 md:px-10 py-10 max-w-7xl mx-auto">
                <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
                    <Stat label="Total messages" value={stats?.contacts_total ?? 0} />
                    <Stat label="New / unread" value={stats?.contacts_new ?? 0} accent />
                    <Stat label="Paid donations" value={stats?.donations_paid_count ?? 0} />
                    <Stat label="Total raised (EUR)" value={`€${(stats?.donations_total_amount ?? 0).toFixed(2)}`} accent />
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                    <TabBtn icon={<Mail size={14} />} active={tab === "contacts"} onClick={() => setTab("contacts")} testid="admin-contacts-tab">
                        Contacts ({contacts.length})
                    </TabBtn>
                    <TabBtn icon={<Coins size={14} />} active={tab === "donations"} onClick={() => setTab("donations")} testid="admin-donations-tab">
                        Donations ({donations.length})
                    </TabBtn>
                    <TabBtn icon={<Newspaper size={14} />} active={tab === "news"} onClick={() => setTab("news")} testid="admin-news-tab">
                        News ({news.length})
                    </TabBtn>
                    <TabBtn icon={<Briefcase size={14} />} active={tab === "activities"} onClick={() => setTab("activities")} testid="admin-activities-tab">
                        Activities ({activities.length})
                    </TabBtn>
                    <TabBtn icon={<Users size={14} />} active={tab === "subscribers"} onClick={() => setTab("subscribers")} testid="admin-subscribers-tab">
                        Subscribers ({subscribers.length})
                    </TabBtn>
                    <TabBtn icon={<BookOpen size={14} />} active={tab === "why"} onClick={() => setTab("why")} testid="admin-why-tab">
                        Why Sport ({whyPoints.length})
                    </TabBtn>
                    <TabBtn icon={<MessageSquare size={14} />} active={tab === "feedback"} onClick={() => setTab("feedback")} testid="admin-feedback-tab">
                        Feedback ({feedback.length})
                    </TabBtn>
                    <TabBtn icon={<FileText size={14} />} active={tab === "copy"} onClick={() => setTab("copy")} testid="admin-copy-tab">
                        Site Copy
                    </TabBtn>
                    <TabBtn icon={<UserCog size={14} />} active={tab === "team"} onClick={() => setTab("team")} testid="admin-team-tab">
                        Team ({teamMembers.length})
                    </TabBtn>
                    <TabBtn icon={<HandHeart size={14} />} active={tab === "support"} onClick={() => setTab("support")} testid="admin-support-tab">
                        Support ({supportOptions.length})
                    </TabBtn>
                    <TabBtn icon={<Handshake size={14} />} active={tab === "partners"} onClick={() => setTab("partners")} testid="admin-partners-tab">
                        Partners
                    </TabBtn>
                </div>

                {tab === "contacts" && <ContactsTable contacts={contacts} />}
                {tab === "donations" && <DonationsTable donations={donations} />}
                {tab === "news" && <NewsManager items={news} onChange={fetchAll} />}
                {tab === "activities" && <ActivitiesManager items={activities} onChange={fetchAll} />}
                {tab === "subscribers" && <SubscribersTable items={subscribers} />}
                {tab === "why" && <WhyManager items={whyPoints} onChange={fetchAll} />}
                {tab === "feedback" && <FeedbackTable items={feedback} />}
                {tab === "copy" && <SiteCopyEditor overrides={overrides} onChange={fetchAll} />}
                {tab === "team" && <TeamManager items={teamMembers} onChange={fetchAll} />}
                {tab === "support" && <SupportManager items={supportOptions} onChange={fetchAll} />}
                {tab === "partners" && <PartnersManager />}
            </main>
        </div>
    );
}

const Stat = ({ label, value, accent }) => (
    <div className="bg-inksurface p-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
        <div className={`font-display font-black text-3xl md:text-4xl mt-2 ${accent ? "text-volt" : "text-white"}`}>{value}</div>
    </div>
);

const TabBtn = ({ icon, active, onClick, children, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold inline-flex items-center gap-2 border transition-colors ${
            active ? "bg-volt text-black border-volt" : "border-white/10 text-zinc-300 hover:border-white/30"
        }`}
    >
        {icon} {children}
    </button>
);

const ContactsTable = ({ contacts }) => (
    <div className="border border-white/10 overflow-x-auto" data-testid="contacts-table">
        <table className="w-full text-sm">
            <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Company</th>
                    <th className="text-left px-4 py-3">Message</th>
                    <th className="text-left px-4 py-3">Status</th>
                </tr>
            </thead>
            <tbody>
                {contacts.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No messages yet.</td></tr>
                )}
                {contacts.map((c) => (
                    <tr key={c.id} className="border-t border-white/5 hover:bg-inksurface/50">
                        <td className="px-4 py-3 text-zinc-400 text-xs font-mono whitespace-nowrap">{c.created_at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-3 text-white">{c.name}</td>
                        <td className="px-4 py-3 text-zinc-300">{c.email}</td>
                        <td className="px-4 py-3 text-zinc-400">{c.company || "—"}</td>
                        <td className="px-4 py-3 text-zinc-300 max-w-md truncate">{c.message}</td>
                        <td className="px-4 py-3">
                            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${
                                c.status === "new" ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-300"
                            }`}>{c.status}</span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const DonationsTable = ({ donations }) => (
    <div className="border border-white/10 overflow-x-auto" data-testid="donations-table">
        <table className="w-full text-sm">
            <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Donor</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Package</th>
                    <th className="text-left px-4 py-3">Status</th>
                </tr>
            </thead>
            <tbody>
                {donations.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No donations yet.</td></tr>
                )}
                {donations.map((d) => (
                    <tr key={d.id} className="border-t border-white/5 hover:bg-inksurface/50">
                        <td className="px-4 py-3 text-zinc-400 text-xs font-mono whitespace-nowrap">{d.created_at?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-4 py-3 text-white">{d.donor_name || "—"}</td>
                        <td className="px-4 py-3 text-zinc-300">{d.donor_email || "—"}</td>
                        <td className="px-4 py-3 text-volt font-display font-bold">€{Number(d.amount).toFixed(2)}</td>
                        <td className="px-4 py-3 text-zinc-400 uppercase text-xs">{d.package_id}</td>
                        <td className="px-4 py-3">
                            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${
                                d.payment_status === "paid" ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-300"
                            }`}>{d.payment_status}</span>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ---------------- News CRUD (multilingual) ----------------
const emptyNews = {
    title_en: "", title_fr: "", title_nl: "",
    excerpt_en: "", excerpt_fr: "", excerpt_nl: "",
    body_en: "", body_fr: "", body_nl: "",
    image_url: "", date: "", external_url: "", published: true, kind: "news",
};

const NewsManager = ({ items, onChange }) => {
    const [editing, setEditing] = useState(null); // null = closed; 'new' = adding; <id> = editing
    const [form, setForm] = useState(emptyNews);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("nl");

    const pickL = (f, l) => (f && typeof f === "object" ? f[l] || "" : f || "");

    const open = (item) => {
        if (item) {
            setForm({
                title_en: pickL(item.title, "en"), title_fr: pickL(item.title, "fr"), title_nl: pickL(item.title, "nl"),
                excerpt_en: pickL(item.excerpt, "en"), excerpt_fr: pickL(item.excerpt, "fr"), excerpt_nl: pickL(item.excerpt, "nl"),
                body_en: pickL(item.body, "en"), body_fr: pickL(item.body, "fr"), body_nl: pickL(item.body, "nl"),
                image_url: item.image_url || "",
                date: item.date || "",
                external_url: item.external_url || "",
                published: item.published ?? true,
                kind: item.kind || "news",
            });
            setEditing(item.id);
        } else {
            setForm(emptyNews);
            setEditing("new");
        }
        setTab("nl");
    };
    const close = () => { setEditing(null); setForm(emptyNews); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                image_url: form.image_url || null,
                date: form.date || null,
                external_url: form.external_url || null,
                published: !!form.published,
                kind: form.kind || "news",
            };
            const cfg = { headers: authHeaders(), withCredentials: true };
            if (editing === "new") {
                await axios.post(`${API}/admin/news`, payload, cfg);
                toast.success("Article created");
            } else {
                await axios.patch(`${API}/admin/news/${editing}`, payload, cfg);
                toast.success("Article updated");
            }
            close();
            onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this article? This cannot be undone.")) return;
        try {
            await axios.delete(`${API}/admin/news/${id}`, { headers: authHeaders(), withCredentials: true });
            toast.success("Deleted");
            onChange();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="news-manager">
            <div className="flex justify-end mb-4">
                <button
                    data-testid="news-new-btn"
                    onClick={() => open(null)}
                    className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2"
                >
                    <Plus size={14} /> New article
                </button>
            </div>

            {editing && (
                <div className="border border-volt/40 bg-inksurface p-6 mb-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-mono uppercase tracking-wider text-volt">
                            {editing === "new" ? "New article" : `Editing #${editing.slice(0, 8)}`}
                        </div>
                        <button onClick={close} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>

                    <Field label="Kind">
                        <select
                            value={form.kind}
                            onChange={(e) => setForm({ ...form, kind: e.target.value })}
                            data-testid="news-kind-select"
                            className="ur-input"
                        >
                            <option value="news">News (announcement / press)</option>
                            <option value="event">Event (gala, tournament, save-the-date)</option>
                        </select>
                    </Field>
                    <Field label="Date (YYYY-MM-DD)">
                        <input value={form.date} placeholder="2025-09-17" onChange={(e) => setForm({ ...form, date: e.target.value })} className="ur-input" />
                    </Field>

                    <div className="flex gap-1 mb-5 border-b border-white/10 mt-6">
                        {["nl", "fr", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setTab(l)}
                                data-testid={`news-lang-${l}`}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors border-b-2 ${
                                    tab === l ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {tab === "nl" && (
                        <>
                            <Field label="Titel (Nederlands)"><input data-testid="news-title-nl" value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="ur-input" /></Field>
                            <Field label="Samenvatting (Nederlands — 1 à 2 regels, op kaarten)"><textarea data-testid="news-excerpt-nl" rows={2} value={form.excerpt_nl} onChange={(e) => setForm({ ...form, excerpt_nl: e.target.value })} className="ur-input resize-none" /></Field>
                            <Field label="Tekst (Nederlands — volledig artikel)"><textarea data-testid="news-body-nl" rows={10} value={form.body_nl} onChange={(e) => setForm({ ...form, body_nl: e.target.value })} className="ur-input resize-y" /></Field>
                        </>
                    )}
                    {tab === "fr" && (
                        <>
                            <Field label="Titre (Français)"><input data-testid="news-title-fr" value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="ur-input" /></Field>
                            <Field label="Résumé (Français — 1 à 2 lignes, sur les cartes)"><textarea data-testid="news-excerpt-fr" rows={2} value={form.excerpt_fr} onChange={(e) => setForm({ ...form, excerpt_fr: e.target.value })} className="ur-input resize-none" /></Field>
                            <Field label="Texte (Français — article complet)"><textarea data-testid="news-body-fr" rows={10} value={form.body_fr} onChange={(e) => setForm({ ...form, body_fr: e.target.value })} className="ur-input resize-y" /></Field>
                        </>
                    )}
                    {tab === "en" && (
                        <>
                            <Field label="Title (English) — required; also generates the article link"><input data-testid="news-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="ur-input" /></Field>
                            <Field label="Excerpt (English — 1–2 lines, shown on cards) — required"><textarea data-testid="news-excerpt-en" rows={2} value={form.excerpt_en} onChange={(e) => setForm({ ...form, excerpt_en: e.target.value })} className="ur-input resize-none" /></Field>
                            <Field label="Body (English — full article) — required"><textarea data-testid="news-body-en" rows={10} value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} className="ur-input resize-y" /></Field>
                        </>
                    )}

                    <div className="border-t border-white/10 pt-5 mt-2">
                        <Field label="Photo (optional — upload from your computer OR paste a URL; you can publish without a photo)">
                            <PhotoField value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
                        </Field>
                        <Field label="External link (optional)">
                            <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" className="ur-input" />
                        </Field>
                        <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                            Published (visible on the site)
                        </label>
                    </div>
                    <div className="mt-5 flex gap-3">
                        <button
                            data-testid="news-save-btn"
                            onClick={save}
                            disabled={saving || !form.title_en || !form.excerpt_en || !form.body_en}
                            className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                        </button>
                        <button onClick={close} className="btn-ghost px-5 py-2 text-xs uppercase font-bold">Cancel</button>
                    </div>
                </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-left px-4 py-3">Kind</th>
                            <th className="text-left px-4 py-3">Title (EN)</th>
                            <th className="text-left px-4 py-3">NL/FR</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No articles yet.</td></tr>
                        )}
                        {items.map((n) => (
                            <tr key={n.id} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-zinc-400 text-xs font-mono whitespace-nowrap">{n.date}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${n.kind === "event" ? "bg-volt/20 text-volt" : "bg-zinc-800 text-zinc-300"}`}>
                                        {n.kind || "news"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-white">{pickL(n.title, "en")}</td>
                                <td className="px-4 py-3 text-xs whitespace-nowrap">
                                    <span className={pickL(n.title, "nl") ? "text-volt" : "text-zinc-600"}>NL {pickL(n.title, "nl") ? "✓" : "—"}</span>
                                    {" "}
                                    <span className={pickL(n.title, "fr") ? "text-volt" : "text-zinc-600"}>FR {pickL(n.title, "fr") ? "✓" : "—"}</span>
                                </td>
                                <td className="px-4 py-3 text-zinc-500 text-xs font-mono">/{n.slug}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${n.published ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>
                                        {n.published ? "live" : "draft"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <button data-testid={`news-edit-${n.id}`} onClick={() => open(n)} className="text-zinc-300 hover:text-volt p-2"><Pencil size={14} /></button>
                                        <button data-testid={`news-delete-${n.id}`} onClick={() => del(n.id)} className="text-zinc-300 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------- Activities CRUD (multilingual) ----------------
const emptyActivity = {
    partner: "",
    title_en: "", title_fr: "", title_nl: "",
    description_en: "", description_fr: "", description_nl: "",
    impact_en: "", impact_fr: "", impact_nl: "",
    image_url: "", external_url: "", order: 0, published: true,
};

const ActivitiesManager = ({ items, onChange }) => {
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyActivity);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("nl");

    const pickL = (f, l) => (f && typeof f === "object" ? f[l] || "" : f || "");

    const open = (item) => {
        if (item) {
            setForm({
                partner: item.partner || "",
                title_en: pickL(item.title, "en"), title_fr: pickL(item.title, "fr"), title_nl: pickL(item.title, "nl"),
                description_en: pickL(item.description, "en"), description_fr: pickL(item.description, "fr"), description_nl: pickL(item.description, "nl"),
                impact_en: pickL(item.impact, "en"), impact_fr: pickL(item.impact, "fr"), impact_nl: pickL(item.impact, "nl"),
                image_url: item.image_url || "",
                external_url: item.external_url || "",
                order: item.order || 0,
                published: item.published ?? true,
            });
            setEditing(item.id);
        } else {
            setForm({ ...emptyActivity, order: items.length + 1 });
            setEditing("new");
        }
        setTab("nl");
    };
    const close = () => { setEditing(null); setForm(emptyActivity); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                impact_en: form.impact_en || null,
                impact_fr: form.impact_fr || null,
                impact_nl: form.impact_nl || null,
                image_url: form.image_url || null,
                external_url: form.external_url || null,
                order: parseInt(form.order || 0, 10),
                published: !!form.published,
            };
            const cfg = { headers: authHeaders(), withCredentials: true };
            if (editing === "new") {
                await axios.post(`${API}/admin/activities`, payload, cfg);
                toast.success("Activity created");
            } else {
                await axios.patch(`${API}/admin/activities/${editing}`, payload, cfg);
                toast.success("Activity updated");
            }
            close();
            onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this activity?")) return;
        try {
            await axios.delete(`${API}/admin/activities/${id}`, { headers: authHeaders(), withCredentials: true });
            toast.success("Deleted");
            onChange();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="activities-manager">
            <div className="flex justify-end mb-4">
                <button data-testid="activity-new-btn" onClick={() => open(null)} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2">
                    <Plus size={14} /> New activity
                </button>
            </div>

            {editing && (
                <div className="border border-volt/40 bg-inksurface p-6 mb-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-mono uppercase tracking-wider text-volt">
                            {editing === "new" ? "New activity" : `Editing #${editing.slice(0, 8)}`}
                        </div>
                        <button onClick={close} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>
                    <Field label="Partner name (e.g. D'Broej, Kids & Family, BX)">
                        <input data-testid="activity-partner-input" value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} className="ur-input" />
                    </Field>

                    <div className="flex gap-1 mb-5 border-b border-white/10 mt-6">
                        {["nl", "fr", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setTab(l)}
                                data-testid={`activity-lang-${l}`}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors border-b-2 ${
                                    tab === l ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {tab === "nl" && (
                        <>
                            <Field label="Titel (Nederlands)"><input data-testid="activity-title-nl" value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="ur-input" /></Field>
                            <Field label="Beschrijving (Nederlands)"><textarea data-testid="activity-desc-nl" rows={3} value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Impact (Nederlands, bv. '41 jongeren · 100% schooljaar voltooid')"><input value={form.impact_nl} onChange={(e) => setForm({ ...form, impact_nl: e.target.value })} className="ur-input" /></Field>
                        </>
                    )}
                    {tab === "fr" && (
                        <>
                            <Field label="Titre (Français)"><input data-testid="activity-title-fr" value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="ur-input" /></Field>
                            <Field label="Description (Français)"><textarea data-testid="activity-desc-fr" rows={3} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Impact (Français, ex. '41 jeunes · 100%')"><input value={form.impact_fr} onChange={(e) => setForm({ ...form, impact_fr: e.target.value })} className="ur-input" /></Field>
                        </>
                    )}
                    {tab === "en" && (
                        <>
                            <Field label="Title (English) — required"><input data-testid="activity-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="ur-input" /></Field>
                            <Field label="Description (English) — required"><textarea data-testid="activity-desc-en" rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Impact (English, e.g. '41 youth · 100% completion')"><input value={form.impact_en} onChange={(e) => setForm({ ...form, impact_en: e.target.value })} className="ur-input" /></Field>
                        </>
                    )}

                    <div className="border-t border-white/10 pt-5 mt-2">
                        <Field label="Photo (optional — upload from your computer OR paste a URL)">
                            <PhotoField value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} />
                        </Field>
                        <Field label="External link (optional)">
                            <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" className="ur-input" />
                        </Field>
                        <Field label="Order (lower = first)">
                            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ur-input" />
                        </Field>
                        <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                            Published
                        </label>
                    </div>
                    <div className="mt-5 flex gap-3">
                        <button
                            data-testid="activity-save-btn"
                            onClick={save}
                            disabled={saving || !form.partner || !form.title_en || !form.description_en}
                            className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                        </button>
                        <button onClick={close} className="btn-ghost px-5 py-2 text-xs uppercase font-bold">Cancel</button>
                    </div>
                </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr>
                            <th className="text-left px-4 py-3 w-16">Order</th>
                            <th className="text-left px-4 py-3">Partner</th>
                            <th className="text-left px-4 py-3">Title (EN)</th>
                            <th className="text-left px-4 py-3">NL/FR</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No activities yet.</td></tr>
                        )}
                        {items.map((a) => (
                            <tr key={a.id} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{a.order}</td>
                                <td className="px-4 py-3 text-volt font-display font-bold">{a.partner}</td>
                                <td className="px-4 py-3 text-white">{pickL(a.title, "en")}</td>
                                <td className="px-4 py-3 text-xs">
                                    <span className={pickL(a.title, "nl") ? "text-volt" : "text-zinc-600"}>NL {pickL(a.title, "nl") ? "✓" : "—"}</span>
                                    {" "}
                                    <span className={pickL(a.title, "fr") ? "text-volt" : "text-zinc-600"}>FR {pickL(a.title, "fr") ? "✓" : "—"}</span>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${a.published ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>
                                        {a.published ? "live" : "draft"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <button data-testid={`activity-edit-${a.id}`} onClick={() => open(a)} className="text-zinc-300 hover:text-volt p-2"><Pencil size={14} /></button>
                                        <button data-testid={`activity-delete-${a.id}`} onClick={() => del(a.id)} className="text-zinc-300 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block mb-4">
        <div className="text-xs uppercase tracking-wider text-zinc-400 mb-2">{label}</div>
        {children}
    </label>
);

// Reusable photo picker: file upload OR URL. Uploads to /api/admin/upload and returns a stable URL.
const PhotoField = ({ value, onChange }) => {
    const [uploading, setUploading] = useState(false);
    const inputRef = React.useRef(null);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const upload = async (file, input) => {
        if (!file) return;
        if (file.size > 12 * 1024 * 1024) {
            toast.error("File too large (max 12 MB)");
            if (input) input.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await axios.post(`${API}/admin/upload`, fd, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
                withCredentials: true,
            });
            onChange(data.url);
            toast.success("Photo uploaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            if (input) input.value = "";
        }
    };

    // Build a display URL that works both for backend-relative uploads and external URLs
    const displayUrl = value?.startsWith("/api/") ? `${BACKEND}${value}` : value;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-4">
                {displayUrl ? (
                    <img
                        src={displayUrl}
                        alt="preview"
                        className="w-20 h-20 object-cover border border-white/10"
                    />
                ) : (
                    <div className="w-20 h-20 border border-dashed border-white/20 flex items-center justify-center text-zinc-600 text-xs">
                        no photo
                    </div>
                )}
                <div className="flex flex-col gap-2 flex-1">
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        data-testid="photo-upload-btn"
                        className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2 w-fit disabled:opacity-50"
                    >
                        {uploading ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                        {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
                    </button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
                        className="sr-only"
                        onChange={(e) => upload(e.target.files?.[0], e.target)}
                    />
                    {value && (
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            className="text-xs text-zinc-400 hover:text-red-400 w-fit"
                        >
                            Remove photo
                        </button>
                    )}
                </div>
            </div>
            <input
                type="text"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="…or paste an image URL"
                className="ur-input text-xs"
            />
            <p className="text-[11px] text-zinc-500">
                JPG, PNG, WebP, GIF or iPhone HEIC · max 12 MB
            </p>
        </div>
    );
};

// ---------------- Site Copy Editor ----------------
const LANG_LIST = ["EN", "FR", "NL"];

const SiteCopyEditor = ({ overrides, onChange }) => {
    const [activeLang, setActiveLang] = useState("EN");
    const [drafts, setDrafts] = useState({}); // { "EN.hero.title": "value" }
    const [openSections, setOpenSections] = useState(() => new Set([COPY_SCHEMA[0]?.section]));
    const [saving, setSaving] = useState(false);

    // Compute the current value for a field (draft > override > default)
    const currentValue = (lang, path) => {
        const key = `${lang}.${path}`;
        if (key in drafts) return drafts[key];
        if (key in overrides) return overrides[key];
        return readPath(copyDefaults[lang] || copyDefaults.EN, path);
    };

    const defaultValue = (lang, path) => readPath(copyDefaults[lang] || copyDefaults.EN, path);

    const setDraft = (lang, path, value) => {
        const key = `${lang}.${path}`;
        setDrafts((d) => ({ ...d, [key]: value }));
    };

    const revertDraft = (lang, path) => {
        const key = `${lang}.${path}`;
        setDrafts((d) => {
            const nd = { ...d };
            delete nd[key];
            return nd;
        });
    };

    const resetToDefault = (lang, path) => {
        // Sending "" deletes the override on the server
        setDraft(lang, path, "");
    };

    const dirty = Object.keys(drafts).length;

    const save = async () => {
        setSaving(true);
        try {
            const updates = Object.entries(drafts).map(([key, value]) => ({ key, value }));
            await axios.put(
                `${API}/admin/copy`,
                { updates },
                { headers: authHeaders(), withCredentials: true }
            );
            toast.success(`Saved ${updates.length} change(s). Refresh public site to see them.`);
            setDrafts({});
            onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const discard = () => setDrafts({});

    const toggleSection = (name) => {
        setOpenSections((s) => {
            const ns = new Set(s);
            if (ns.has(name)) ns.delete(name);
            else ns.add(name);
            return ns;
        });
    };

    const expandAll = () => setOpenSections(new Set(COPY_SCHEMA.map((s) => s.section)));
    const collapseAll = () => setOpenSections(new Set());

    return (
        <div data-testid="site-copy-editor">
            {/* Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 sticky top-0 bg-ink pt-2 pb-4 z-10 border-b border-white/5">
                <div className="flex items-center gap-2">
                    {/* Language tabs */}
                    <div className="flex gap-1 border border-white/10">
                        {LANG_LIST.map((l) => (
                            <button
                                key={l}
                                data-testid={`copy-lang-${l.toLowerCase()}`}
                                onClick={() => setActiveLang(l)}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors ${
                                    activeLang === l ? "bg-volt text-black" : "text-zinc-400 hover:text-white"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <button onClick={expandAll} className="text-xs text-zinc-400 hover:text-volt px-3 py-2">Expand all</button>
                    <button onClick={collapseAll} className="text-xs text-zinc-400 hover:text-volt px-3 py-2">Collapse all</button>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono uppercase tracking-wider ${dirty ? "text-volt" : "text-zinc-500"}`}>
                        {dirty ? `${dirty} unsaved change${dirty > 1 ? "s" : ""}` : "No changes"}
                    </span>
                    <button
                        onClick={discard}
                        disabled={!dirty}
                        className="btn-ghost px-4 py-2 text-xs uppercase font-bold disabled:opacity-40"
                    >
                        Discard
                    </button>
                    <button
                        data-testid="copy-save-btn"
                        onClick={save}
                        disabled={!dirty || saving}
                        className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save all
                    </button>
                </div>
            </div>

            <p className="text-xs text-zinc-500 mb-4">
                Tip: leave a field blank to revert to the default text. Click a section header to expand it.
                Changes only affect the currently-selected language ({activeLang}).
            </p>

            {/* Sections */}
            <div className="space-y-3">
                {COPY_SCHEMA.map((sect) => {
                    const isOpen = openSections.has(sect.section);
                    const editsInSection = sect.keys.filter((k) => `${activeLang}.${k.path}` in drafts).length;
                    return (
                        <div key={sect.section} className="border border-white/10">
                            <button
                                onClick={() => toggleSection(sect.section)}
                                data-testid={`copy-section-${sect.section.replace(/\s+/g, "-").toLowerCase()}`}
                                className="w-full bg-inksurface px-4 py-3 flex items-center justify-between text-left hover:bg-inksurface/60"
                            >
                                <div className="flex items-center gap-3">
                                    {isOpen ? <ChevronDown size={14} className="text-volt" /> : <ChevronRight size={14} className="text-zinc-500" />}
                                    <span className="font-display font-bold uppercase tracking-wider text-sm text-white">{sect.section}</span>
                                    <span className="text-[10px] font-mono text-zinc-500">{sect.keys.length} fields</span>
                                </div>
                                {editsInSection > 0 && (
                                    <span className="text-[10px] font-mono uppercase text-volt border border-volt/40 px-2 py-1">
                                        {editsInSection} edited
                                    </span>
                                )}
                            </button>
                            {isOpen && (
                                <div className="p-4 md:p-6 space-y-5 bg-ink">
                                    {sect.keys.map((k) => {
                                        const key = `${activeLang}.${k.path}`;
                                        const val = currentValue(activeLang, k.path);
                                        const def = defaultValue(activeLang, k.path);
                                        const overridden = key in overrides || key in drafts;
                                        const dirtyField = key in drafts;
                                        return (
                                            <div key={k.path} className="border-b border-white/5 pb-4 last:border-b-0 last:pb-0">
                                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <code className="text-[11px] font-mono text-zinc-400">{k.path}</code>
                                                        {overridden && (
                                                            <span className="text-[9px] font-mono uppercase text-volt border border-volt/30 px-1.5 py-0.5">
                                                                {dirtyField ? "unsaved" : "custom"}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-3 text-[10px]">
                                                        {dirtyField && (
                                                            <button
                                                                onClick={() => revertDraft(activeLang, k.path)}
                                                                className="text-zinc-400 hover:text-white inline-flex items-center gap-1"
                                                            >
                                                                <RefreshCw size={10} /> undo
                                                            </button>
                                                        )}
                                                        {overridden && !dirtyField && (
                                                            <button
                                                                onClick={() => resetToDefault(activeLang, k.path)}
                                                                className="text-zinc-400 hover:text-red-400 inline-flex items-center gap-1"
                                                            >
                                                                <RefreshCw size={10} /> reset to default
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {k.note && (
                                                    <div className="text-[11px] text-zinc-500 mb-2 italic">{k.note}</div>
                                                )}
                                                {k.type === "textarea" ? (
                                                    <textarea
                                                        data-testid={`copy-field-${activeLang}-${k.path}`}
                                                        rows={3}
                                                        value={val}
                                                        onChange={(e) => setDraft(activeLang, k.path, e.target.value)}
                                                        className="ur-input resize-y"
                                                    />
                                                ) : (
                                                    <input
                                                        data-testid={`copy-field-${activeLang}-${k.path}`}
                                                        value={val}
                                                        onChange={(e) => setDraft(activeLang, k.path, e.target.value)}
                                                        className="ur-input"
                                                    />
                                                )}
                                                {def && val !== def && (
                                                    <div className="text-[10px] font-mono text-zinc-600 mt-1 truncate">
                                                        default: {def.length > 100 ? def.slice(0, 100) + "…" : def}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ---------------- Feedback table ----------------
const FeedbackTable = ({ items }) => {
    const avg = items.length ? (items.reduce((a, b) => a + (b.rating || 0), 0) / items.length).toFixed(1) : "—";
    return (
        <div data-testid="feedback-table">
            <div className="mb-4 text-sm text-zinc-400">
                <span className="text-white font-display font-bold">{items.length}</span> feedback entries · Average rating:{" "}
                <span className="text-volt font-display font-bold">{avg}</span> / 5
            </div>
            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Rating</th>
                            <th className="text-left px-4 py-3">Section</th>
                            <th className="text-left px-4 py-3">Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No feedback yet.</td></tr>
                        )}
                        {items.map((f) => (
                            <tr key={f.id} className="border-t border-white/5 hover:bg-inksurface/50 align-top">
                                <td className="px-4 py-3 text-zinc-400 text-xs font-mono whitespace-nowrap">{f.created_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="px-4 py-3 text-white">{f.name}</td>
                                <td className="px-4 py-3 text-zinc-300">{f.email || "—"}</td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} size={14} className={n <= (f.rating || 0) ? "text-volt fill-volt" : "text-zinc-700"} />
                                        ))}
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-zinc-400">{f.section || "—"}</td>
                                <td className="px-4 py-3 text-zinc-300 max-w-md whitespace-pre-wrap">{f.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------- Why Sport CRUD (multilingual) ----------------
const emptyWhy = {
    title_en: "", title_fr: "", title_nl: "",
    body_en: "", body_fr: "", body_nl: "",
    image_url: "", cta_text: "", cta_link: "",
    order: 0, published: true,
};

const WhyManager = ({ items, onChange }) => {
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyWhy);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("en");

    const open = (item) => {
        if (item) {
            setForm({
                title_en: item.title?.en || "",
                title_fr: item.title?.fr || "",
                title_nl: item.title?.nl || "",
                body_en: item.body?.en || "",
                body_fr: item.body?.fr || "",
                body_nl: item.body?.nl || "",
                image_url: item.image_url || "",
                cta_text: item.cta_text || "",
                cta_link: item.cta_link || "",
                order: item.order || 0,
                published: item.published ?? true,
            });
            setEditing(item.id);
        } else {
            setForm({ ...emptyWhy, order: items.length + 1 });
            setEditing("new");
        }
        setTab("en");
    };
    const close = () => { setEditing(null); setForm(emptyWhy); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: { en: form.title_en, fr: form.title_fr, nl: form.title_nl },
                body: { en: form.body_en, fr: form.body_fr, nl: form.body_nl },
                image_url: form.image_url || null,
                cta_text: form.cta_text || null,
                cta_link: form.cta_link || null,
                order: parseInt(form.order || 0, 10),
                published: !!form.published,
            };
            const cfg = { headers: authHeaders(), withCredentials: true };
            if (editing === "new") {
                await axios.post(`${API}/admin/why-sport`, payload, cfg);
                toast.success("Snippet created");
            } else {
                await axios.patch(`${API}/admin/why-sport/${editing}`, payload, cfg);
                toast.success("Snippet updated");
            }
            close();
            onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this snippet?")) return;
        try {
            await axios.delete(`${API}/admin/why-sport/${id}`, { headers: authHeaders(), withCredentials: true });
            toast.success("Deleted");
            onChange();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="why-manager">
            <div className="flex justify-end mb-4">
                <button data-testid="why-new-btn" onClick={() => open(null)} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2">
                    <Plus size={14} /> New snippet
                </button>
            </div>

            {editing && (
                <div className="border border-volt/40 bg-inksurface p-6 mb-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-mono uppercase tracking-wider text-volt">
                            {editing === "new" ? "New snippet" : `Editing #${editing.slice(0, 8)}`}
                        </div>
                        <button onClick={close} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>

                    {/* Language tabs */}
                    <div className="flex gap-1 mb-5 border-b border-white/10">
                        {["en", "fr", "nl"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setTab(l)}
                                data-testid={`why-lang-${l}`}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors border-b-2 ${
                                    tab === l ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {tab === "en" && (
                        <>
                            <Field label="Headline (English)">
                                <input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="ur-input" />
                            </Field>
                            <Field label="Body (English)">
                                <textarea rows={5} value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} className="ur-input resize-y" />
                            </Field>
                        </>
                    )}
                    {tab === "fr" && (
                        <>
                            <Field label="Titre (Français)">
                                <input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="ur-input" />
                            </Field>
                            <Field label="Corps (Français)">
                                <textarea rows={5} value={form.body_fr} onChange={(e) => setForm({ ...form, body_fr: e.target.value })} className="ur-input resize-y" />
                            </Field>
                        </>
                    )}
                    {tab === "nl" && (
                        <>
                            <Field label="Titel (Nederlands)">
                                <input value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="ur-input" />
                            </Field>
                            <Field label="Tekst (Nederlands)">
                                <textarea rows={5} value={form.body_nl} onChange={(e) => setForm({ ...form, body_nl: e.target.value })} className="ur-input resize-y" />
                            </Field>
                        </>
                    )}

                    <div className="border-t border-white/10 pt-5 mt-5">
                        <Field label="Image URL (used as side photo on /why-sport)">
                            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="ur-input" />
                        </Field>
                        <Field label="CTA button label (optional, e.g. 'Become a mentor')">
                            <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="ur-input" />
                        </Field>
                        <Field label="CTA link (optional, e.g. '#donate' or 'https://...')">
                            <input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="ur-input" />
                        </Field>
                        <Field label="Order (lower = first)">
                            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ur-input" />
                        </Field>
                        <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                            Published
                        </label>
                    </div>

                    <div className="mt-5 flex gap-3">
                        <button
                            data-testid="why-save-btn"
                            onClick={save}
                            disabled={saving || !form.title_en || !form.body_en}
                            className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                        </button>
                        <button onClick={close} className="btn-ghost px-5 py-2 text-xs uppercase font-bold">Cancel</button>
                    </div>
                </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr>
                            <th className="text-left px-4 py-3 w-16">Order</th>
                            <th className="text-left px-4 py-3">Headline (EN)</th>
                            <th className="text-left px-4 py-3">FR</th>
                            <th className="text-left px-4 py-3">NL</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No snippets yet.</td></tr>
                        )}
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{p.order}</td>
                                <td className="px-4 py-3 text-white">{p.title?.en}</td>
                                <td className="px-4 py-3 text-center text-xs">{p.title?.fr ? <span className="text-volt">✓</span> : <span className="text-zinc-600">—</span>}</td>
                                <td className="px-4 py-3 text-center text-xs">{p.title?.nl ? <span className="text-volt">✓</span> : <span className="text-zinc-600">—</span>}</td>
                                <td className="px-4 py-3 text-zinc-500 text-xs font-mono">/{p.slug}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${p.published ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>
                                        {p.published ? "live" : "draft"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex gap-2">
                                        <button data-testid={`why-edit-${p.id}`} onClick={() => open(p)} className="text-zinc-300 hover:text-volt p-2"><Pencil size={14} /></button>
                                        <button data-testid={`why-delete-${p.id}`} onClick={() => del(p.id)} className="text-zinc-300 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------- Subscribers (unified) ----------------
const SubscribersTable = ({ items }) => {
    const downloadCsv = async () => {
        try {
            const res = await axios.get(`${API}/admin/subscribers.csv`, {
                headers: authHeaders(),
                withCredentials: true,
                responseType: "blob",
            });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "urban_rookies_subscribers.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("CSV exported");
        } catch (_) {
            toast.error("Export failed");
        }
    };

    const consented = items.filter((i) => i.consent).length;

    return (
        <div data-testid="subscribers-manager">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="text-sm text-zinc-400">
                    <span className="text-white font-display font-bold">{items.length}</span> unique emails ·{" "}
                    <span className="text-volt">{consented}</span> with explicit newsletter consent
                </div>
                <button
                    data-testid="subscribers-export-csv"
                    onClick={downloadCsv}
                    className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2"
                >
                    <Download size={14} /> Export CSV
                </button>
            </div>

            <p className="text-xs text-zinc-500 mb-4">
                Tip: import this CSV into <span className="text-zinc-300">Brevo, Mailchimp or MailerLite</span> to run email campaigns. Only emails tagged{" "}
                <span className="text-volt font-mono">consent=yes</span> should be used for marketing campaigns under GDPR.
            </p>

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3">Sources</th>
                            <th className="text-left px-4 py-3">Newsletter consent</th>
                            <th className="text-left px-4 py-3">First seen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No subscribers yet.</td></tr>
                        )}
                        {items.map((s) => (
                            <tr key={s.email} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-white">{s.email}</td>
                                <td className="px-4 py-3 text-zinc-300">{s.name || "—"}</td>
                                <td className="px-4 py-3">
                                    <div className="flex gap-1 flex-wrap">
                                        {(s.sources || []).map((src) => (
                                            <span key={src} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-zinc-800 text-zinc-300">
                                                {src}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${s.consent ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>
                                        {s.consent ? "yes" : "no"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-zinc-400 text-xs font-mono whitespace-nowrap">
                                    {s.first_seen?.slice(0, 16).replace("T", " ")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// ---------------- Team Members CRUD ----------------
const TEAM_CATS = ["founder", "board", "operational", "ambassadors", "advisors"];
const CAT_LABELS = { founder: "Founder", board: "Board", operational: "Operational", ambassadors: "Ambassadors", advisors: "Advisors" };
const emptyMember = {
    category: "operational", name: "",
    role_en: "", role_fr: "", role_nl: "",
    photo_url: "", linkedin: "",
    bio_en: "", bio_fr: "", bio_nl: "",
    motivation_en: "", motivation_fr: "", motivation_nl: "",
    order: 0, published: true,
};

const TeamManager = ({ items, onChange }) => {
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyMember);
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState("all");
    const [tab, setTab] = useState("nl");

    const pickL = (f, l) => (f && typeof f === "object" ? f[l] || "" : f || "");

    const open = (item) => {
        if (item) {
            setForm({
                category: item.category || "operational",
                name: item.name || "",
                role_en: pickL(item.role, "en"), role_fr: pickL(item.role, "fr"), role_nl: pickL(item.role, "nl"),
                photo_url: item.photo_url || "",
                linkedin: item.linkedin || "",
                bio_en: pickL(item.bio, "en"), bio_fr: pickL(item.bio, "fr"), bio_nl: pickL(item.bio, "nl"),
                motivation_en: pickL(item.motivation, "en"), motivation_fr: pickL(item.motivation, "fr"), motivation_nl: pickL(item.motivation, "nl"),
                order: item.order || 0,
                published: item.published ?? true,
            });
            setEditing(item.id);
        } else {
            setForm({ ...emptyMember, order: items.length + 1 });
            setEditing("new");
        }
        setTab("nl");
    };
    const close = () => { setEditing(null); setForm(emptyMember); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                role_en: form.role_en || null, role_fr: form.role_fr || null, role_nl: form.role_nl || null,
                photo_url: form.photo_url || null,
                linkedin: form.linkedin || null,
                bio_en: form.bio_en || null, bio_fr: form.bio_fr || null, bio_nl: form.bio_nl || null,
                motivation_en: form.motivation_en || null, motivation_fr: form.motivation_fr || null, motivation_nl: form.motivation_nl || null,
                order: parseInt(form.order || 0, 10),
            };
            const cfg = { headers: authHeaders(), withCredentials: true };
            if (editing === "new") await axios.post(`${API}/admin/team`, payload, cfg);
            else await axios.patch(`${API}/admin/team/${editing}`, payload, cfg);
            toast.success("Saved");
            close();
            onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this member?")) return;
        try { await axios.delete(`${API}/admin/team/${id}`, { headers: authHeaders(), withCredentials: true }); toast.success("Deleted"); onChange(); }
        catch { toast.error("Delete failed"); }
    };

    const filtered = filter === "all" ? items : items.filter((m) => m.category === filter);

    return (
        <div data-testid="team-manager">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex gap-1 flex-wrap">
                    {["all", ...TEAM_CATS].map((c) => (
                        <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 text-xs uppercase font-mono tracking-wider border ${filter === c ? "bg-volt text-black border-volt" : "border-white/10 text-zinc-400 hover:text-white"}`}>
                            {c === "all" ? "All" : CAT_LABELS[c]}
                        </button>
                    ))}
                </div>
                <button data-testid="team-new-btn" onClick={() => open(null)} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2">
                    <Plus size={14} /> New member
                </button>
            </div>

            {editing && (
                <div className="border border-volt/40 bg-inksurface p-6 mb-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-mono uppercase tracking-wider text-volt">{editing === "new" ? "New member" : `Editing #${editing.slice(0, 8)}`}</div>
                        <button onClick={close} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>
                    <Field label="Category">
                        <div data-testid="team-category-options" role="radiogroup" aria-label="Team category" className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            {TEAM_CATS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    data-testid={`team-category-${c}`}
                                    aria-pressed={form.category === c}
                                    onClick={() => setForm({ ...form, category: c })}
                                    className={`px-3 py-3 text-xs uppercase font-mono tracking-wider border transition-colors ${
                                        form.category === c
                                            ? "bg-volt text-black border-volt"
                                            : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                                    }`}
                                >
                                    {CAT_LABELS[c]}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <Field label="Name"><input data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ur-input" /></Field>
                    <Field label="Photo (upload from your computer OR paste a URL)">
                        <PhotoField
                            value={form.photo_url}
                            onChange={(v) => setForm({ ...form, photo_url: v })}
                        />
                    </Field>
                    <Field label="LinkedIn URL"><input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="https://linkedin.com/in/…" className="ur-input" /></Field>

                    <div className="flex gap-1 mb-5 border-b border-white/10 mt-6">
                        {["nl", "fr", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setTab(l)}
                                data-testid={`team-lang-${l}`}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors border-b-2 ${
                                    tab === l ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {tab === "nl" && (
                        <>
                            <Field label="Rol (Nederlands — bv. Lid van de Raad van Bestuur)"><input data-testid="team-role-nl" value={form.role_nl} onChange={(e) => setForm({ ...form, role_nl: e.target.value })} className="ur-input" /></Field>
                            <Field label="Motivational one-liner (Nederlands — op de kaart, onder de naam)"><textarea data-testid="team-motivation-nl" rows={2} value={form.motivation_nl} onChange={(e) => setForm({ ...form, motivation_nl: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Bio (Nederlands — zichtbaar in de pop-up bij klik)"><textarea data-testid="team-bio-nl" rows={4} value={form.bio_nl} onChange={(e) => setForm({ ...form, bio_nl: e.target.value })} className="ur-input resize-y" /></Field>
                        </>
                    )}
                    {tab === "fr" && (
                        <>
                            <Field label="Rôle (Français)"><input data-testid="team-role-fr" value={form.role_fr} onChange={(e) => setForm({ ...form, role_fr: e.target.value })} className="ur-input" /></Field>
                            <Field label="Phrase de motivation (Français — sur la carte)"><textarea data-testid="team-motivation-fr" rows={2} value={form.motivation_fr} onChange={(e) => setForm({ ...form, motivation_fr: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Bio (Français — visible dans la pop-up au clic)"><textarea data-testid="team-bio-fr" rows={4} value={form.bio_fr} onChange={(e) => setForm({ ...form, bio_fr: e.target.value })} className="ur-input resize-y" /></Field>
                        </>
                    )}
                    {tab === "en" && (
                        <>
                            <Field label="Role (English — e.g. CEO, CFO, Ambassador)"><input data-testid="team-role-en" value={form.role_en} onChange={(e) => setForm({ ...form, role_en: e.target.value })} className="ur-input" /></Field>
                            <Field label="Motivational one-liner (English — shown on the card, under the name)"><textarea data-testid="team-motivation-en" rows={2} value={form.motivation_en} onChange={(e) => setForm({ ...form, motivation_en: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Bio (English — shown in the pop-up when you click the person)"><textarea data-testid="team-bio-en" rows={4} value={form.bio_en} onChange={(e) => setForm({ ...form, bio_en: e.target.value })} className="ur-input resize-y" /></Field>
                        </>
                    )}

                    <div className="border-t border-white/10 pt-5 mt-2">
                        <Field label="Order (within category, lower = first)"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ur-input" /></Field>
                        <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
                        </label>
                    </div>
                    <div className="mt-5 flex gap-3">
                        <button data-testid="team-save-btn" onClick={save} disabled={saving || !form.name} className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                        </button>
                        <button onClick={close} className="btn-ghost px-5 py-2 text-xs uppercase font-bold">Cancel</button>
                    </div>
                </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr><th className="text-left px-4 py-3 w-16">Order</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Role (EN)</th><th className="text-left px-4 py-3">NL/FR</th><th className="text-left px-4 py-3">Photo</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No members.</td></tr>}
                        {filtered.map((m) => (
                            <tr key={m.id} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{m.order}</td>
                                <td className="px-4 py-3"><span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-zinc-800 text-zinc-300">{CAT_LABELS[m.category]}</span></td>
                                <td className="px-4 py-3 text-white">{m.name}</td>
                                <td className="px-4 py-3 text-zinc-400 text-xs">{pickL(m.role, "en") || "—"}</td>
                                <td className="px-4 py-3 text-xs whitespace-nowrap">
                                    <span className={pickL(m.role, "nl") || pickL(m.bio, "nl") || pickL(m.motivation, "nl") ? "text-volt" : "text-zinc-600"}>NL {(pickL(m.role, "nl") || pickL(m.bio, "nl") || pickL(m.motivation, "nl")) ? "✓" : "—"}</span>
                                    {" "}
                                    <span className={pickL(m.role, "fr") || pickL(m.bio, "fr") || pickL(m.motivation, "fr") ? "text-volt" : "text-zinc-600"}>FR {(pickL(m.role, "fr") || pickL(m.bio, "fr") || pickL(m.motivation, "fr")) ? "✓" : "—"}</span>
                                </td>
                                <td className="px-4 py-3 text-xs">{m.photo_url ? <span className="text-volt">✓</span> : <span className="text-zinc-600">—</span>}</td>
                                <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${m.published ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>{m.published ? "live" : "draft"}</span></td>
                                <td className="px-4 py-3 text-right"><div className="inline-flex gap-2">
                                    <button data-testid={`team-edit-${m.id}`} onClick={() => open(m)} className="text-zinc-300 hover:text-volt p-2"><Pencil size={14} /></button>
                                    <button data-testid={`team-delete-${m.id}`} onClick={() => del(m.id)} className="text-zinc-300 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                                </div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------- Support Options CRUD (multilingual) ----------------
const ICON_CHOICES = ["ShieldCheck", "Users", "Briefcase", "Calendar", "Megaphone", "Heart"];
const emptySupport = {
    title_en: "", title_fr: "", title_nl: "",
    description_en: "", description_fr: "", description_nl: "",
    cta_text_en: "", cta_text_fr: "", cta_text_nl: "",
    cta_link: "", icon: "Heart", highlight: false, order: 0, published: true,
};

const SupportManager = ({ items, onChange }) => {
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptySupport);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("nl");

    const pickL = (f, l) => (f && typeof f === "object" ? f[l] || "" : f || "");

    const open = (item) => {
        if (item) {
            setForm({
                title_en: pickL(item.title, "en"), title_fr: pickL(item.title, "fr"), title_nl: pickL(item.title, "nl"),
                description_en: pickL(item.description, "en"), description_fr: pickL(item.description, "fr"), description_nl: pickL(item.description, "nl"),
                cta_text_en: pickL(item.cta_text, "en"), cta_text_fr: pickL(item.cta_text, "fr"), cta_text_nl: pickL(item.cta_text, "nl"),
                cta_link: item.cta_link || "",
                icon: item.icon || "Heart",
                highlight: item.highlight ?? false,
                order: item.order || 0,
                published: item.published ?? true,
            });
            setEditing(item.id);
        } else { setForm({ ...emptySupport, order: items.length + 1 }); setEditing("new"); }
        setTab("nl");
    };
    const close = () => { setEditing(null); setForm(emptySupport); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form, order: parseInt(form.order || 0, 10) };
            const cfg = { headers: authHeaders(), withCredentials: true };
            if (editing === "new") await axios.post(`${API}/admin/support`, payload, cfg);
            else await axios.patch(`${API}/admin/support/${editing}`, payload, cfg);
            toast.success("Saved");
            close(); onChange();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this support option?")) return;
        try { await axios.delete(`${API}/admin/support/${id}`, { headers: authHeaders(), withCredentials: true }); toast.success("Deleted"); onChange(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="support-manager">
            <div className="flex justify-end mb-4">
                <button data-testid="support-new-btn" onClick={() => open(null)} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2"><Plus size={14} /> New support option</button>
            </div>
            {editing && (
                <div className="border border-volt/40 bg-inksurface p-6 mb-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-mono uppercase tracking-wider text-volt">{editing === "new" ? "New option" : `Editing #${editing.slice(0, 8)}`}</div>
                        <button onClick={close} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>

                    <div className="flex gap-1 mb-5 border-b border-white/10">
                        {["nl", "fr", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setTab(l)}
                                data-testid={`support-lang-${l}`}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors border-b-2 ${
                                    tab === l ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    {tab === "nl" && (
                        <>
                            <Field label="Titel (Nederlands)"><input data-testid="support-title-nl" value={form.title_nl} onChange={(e) => setForm({ ...form, title_nl: e.target.value })} className="ur-input" /></Field>
                            <Field label="Beschrijving (Nederlands)"><textarea data-testid="support-desc-nl" rows={3} value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Knoptekst (Nederlands)"><input data-testid="support-cta-nl" value={form.cta_text_nl} onChange={(e) => setForm({ ...form, cta_text_nl: e.target.value })} className="ur-input" /></Field>
                        </>
                    )}
                    {tab === "fr" && (
                        <>
                            <Field label="Titre (Français)"><input data-testid="support-title-fr" value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} className="ur-input" /></Field>
                            <Field label="Description (Français)"><textarea data-testid="support-desc-fr" rows={3} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="Texte du bouton (Français)"><input data-testid="support-cta-fr" value={form.cta_text_fr} onChange={(e) => setForm({ ...form, cta_text_fr: e.target.value })} className="ur-input" /></Field>
                        </>
                    )}
                    {tab === "en" && (
                        <>
                            <Field label="Title (English) — required"><input data-testid="support-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="ur-input" /></Field>
                            <Field label="Description (English) — required"><textarea data-testid="support-desc-en" rows={3} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="ur-input resize-y" /></Field>
                            <Field label="CTA button text (English) — required"><input data-testid="support-cta-en" value={form.cta_text_en} onChange={(e) => setForm({ ...form, cta_text_en: e.target.value })} className="ur-input" /></Field>
                        </>
                    )}

                    <div className="border-t border-white/10 pt-5 mt-2">
                        <Field label="CTA link (URL or #anchor)"><input data-testid="support-link-input" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="ur-input" /></Field>
                        <Field label="Icon">
                            <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="ur-input">
                                {ICON_CHOICES.map((i) => <option key={i}>{i}</option>)}
                            </select>
                        </Field>
                        <Field label="Order"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ur-input" /></Field>
                        <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                            <input type="checkbox" checked={!!form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} /> Highlight (primary hero card — only 1 recommended)
                        </label>
                        <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
                        </label>
                    </div>
                    <div className="mt-5 flex gap-3">
                        <button data-testid="support-save-btn" onClick={save} disabled={saving || !form.title_en || !form.description_en || !form.cta_text_en || !form.cta_link} className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                        </button>
                        <button onClick={close} className="btn-ghost px-5 py-2 text-xs uppercase font-bold">Cancel</button>
                    </div>
                </div>
            )}
            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr><th className="text-left px-4 py-3 w-16">Order</th><th className="text-left px-4 py-3">Title (EN)</th><th className="text-left px-4 py-3">NL/FR</th><th className="text-left px-4 py-3">CTA</th><th className="text-left px-4 py-3">Icon</th><th className="text-left px-4 py-3">Highlight</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No options.</td></tr>}
                        {items.map((o) => (
                            <tr key={o.id} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{o.order}</td>
                                <td className="px-4 py-3 text-white">{pickL(o.title, "en")}</td>
                                <td className="px-4 py-3 text-xs whitespace-nowrap">
                                    <span className={pickL(o.title, "nl") ? "text-volt" : "text-zinc-600"}>NL {pickL(o.title, "nl") ? "✓" : "—"}</span>
                                    {" "}
                                    <span className={pickL(o.title, "fr") ? "text-volt" : "text-zinc-600"}>FR {pickL(o.title, "fr") ? "✓" : "—"}</span>
                                </td>
                                <td className="px-4 py-3 text-zinc-400 text-xs">{pickL(o.cta_text, "en")} → {o.cta_link}</td>
                                <td className="px-4 py-3 text-zinc-400 text-xs font-mono">{o.icon}</td>
                                <td className="px-4 py-3 text-xs">{o.highlight ? <span className="text-volt">★</span> : "—"}</td>
                                <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${o.published ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>{o.published ? "live" : "draft"}</span></td>
                                <td className="px-4 py-3 text-right"><div className="inline-flex gap-2">
                                    <button data-testid={`support-edit-${o.id}`} onClick={() => open(o)} className="text-zinc-300 hover:text-volt p-2"><Pencil size={14} /></button>
                                    <button onClick={() => del(o.id)} className="text-zinc-300 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                                </div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------- Partners CRUD ----------------
const emptyPartner = { name: "", website_url: "", logo_url: "", description_en: "", description_fr: "", description_nl: "", order: 0, published: true };

const PartnersManager = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyPartner);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("nl");

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/partners`, { headers: authHeaders(), withCredentials: true });
            setItems(data);
        } catch { toast.error("Failed to load partners"); }
    };
    useEffect(() => { load(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

    const open = (item) => {
        if (item) {
            setForm({
                name: item.name || "",
                website_url: item.website_url || "",
                logo_url: item.logo_url || "",
                description_en: item.description?.en || "",
                description_fr: item.description?.fr || "",
                description_nl: item.description?.nl || "",
                order: item.order || 0,
                published: item.published ?? true,
            });
            setEditing(item.id);
        } else {
            setForm({ ...emptyPartner, order: items.length + 1 });
            setEditing("new");
        }
        setTab("nl");
    };
    const close = () => { setEditing(null); setForm(emptyPartner); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                website_url: form.website_url || null,
                logo_url: form.logo_url || null,
                order: parseInt(form.order || 0, 10),
            };
            const cfg = { headers: authHeaders(), withCredentials: true };
            if (editing === "new") await axios.post(`${API}/admin/partners`, payload, cfg);
            else await axios.patch(`${API}/admin/partners/${editing}`, payload, cfg);
            toast.success("Saved");
            close();
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this partner?")) return;
        try { await axios.delete(`${API}/admin/partners/${id}`, { headers: authHeaders(), withCredentials: true }); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="partners-manager">
            <div className="flex justify-end mb-4">
                <button data-testid="partners-new-btn" onClick={() => open(null)} className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-2">
                    <Plus size={14} /> New partner
                </button>
            </div>

            {editing && (
                <div className="border border-volt/40 bg-inksurface p-6 mb-6">
                    <div className="flex items-center justify-between mb-5">
                        <div className="text-xs font-mono uppercase tracking-wider text-volt">{editing === "new" ? "New partner" : `Editing #${editing.slice(0, 8)}`}</div>
                        <button onClick={close} className="text-zinc-400 hover:text-white"><X size={18} /></button>
                    </div>
                    <Field label="Partner name"><input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ur-input" /></Field>
                    <Field label="Website / social URL (logo and name link here)"><input data-testid="partner-url-input" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://…" className="ur-input" /></Field>
                    <Field label="Logo (upload from your computer OR paste a URL)">
                        <PhotoField value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} />
                    </Field>

                    <div className="flex gap-1 mb-5 border-b border-white/10 mt-6">
                        {["nl", "fr", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setTab(l)}
                                data-testid={`partner-lang-${l}`}
                                className={`px-4 py-2 text-xs uppercase font-mono tracking-wider transition-colors border-b-2 ${
                                    tab === l ? "border-volt text-volt" : "border-transparent text-zinc-500 hover:text-zinc-300"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <div className="text-xs text-zinc-500 mb-4 -mt-2">Short description of the partnership — max. ~20 lines. Shown on the site next to the logo.</div>
                    {tab === "nl" && (
                        <Field label="Beschrijving (Nederlands)">
                            <textarea data-testid="partner-desc-nl" rows={8} maxLength={2000} value={form.description_nl} onChange={(e) => setForm({ ...form, description_nl: e.target.value })} className="ur-input resize-y" />
                        </Field>
                    )}
                    {tab === "fr" && (
                        <Field label="Description (Français)">
                            <textarea data-testid="partner-desc-fr" rows={8} maxLength={2000} value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} className="ur-input resize-y" />
                        </Field>
                    )}
                    {tab === "en" && (
                        <Field label="Description (English)">
                            <textarea data-testid="partner-desc-en" rows={8} maxLength={2000} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="ur-input resize-y" />
                        </Field>
                    )}

                    <Field label="Order (lower = first)"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ur-input" /></Field>
                    <label className="flex items-center gap-2 text-sm text-zinc-300 mt-2">
                        <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
                    </label>
                    <div className="mt-5 flex gap-3">
                        <button data-testid="partner-save-btn" onClick={save} disabled={saving || !form.name} className="btn-volt px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                        </button>
                        <button onClick={close} className="btn-ghost px-5 py-2 text-xs uppercase font-bold">Cancel</button>
                    </div>
                </div>
            )}

            <div className="border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-inksurface text-xs uppercase tracking-wider text-zinc-400">
                        <tr><th className="text-left px-4 py-3 w-16">Order</th><th className="text-left px-4 py-3">Logo</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">URL</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No partners yet.</td></tr>}
                        {items.map((p) => (
                            <tr key={p.id} data-testid={`partner-row-${p.id}`} className="border-t border-white/5 hover:bg-inksurface/50">
                                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{p.order}</td>
                                <td className="px-4 py-3 text-xs">{p.logo_url ? <span className="text-volt">✓</span> : <span className="text-zinc-600">—</span>}</td>
                                <td className="px-4 py-3 text-white">{p.name}</td>
                                <td className="px-4 py-3 text-zinc-400 text-xs max-w-[220px] truncate">{p.website_url || "—"}</td>
                                <td className="px-4 py-3"><span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 ${p.published ? "bg-volt/20 text-volt" : "bg-zinc-700 text-zinc-400"}`}>{p.published ? "live" : "draft"}</span></td>
                                <td className="px-4 py-3 text-right"><div className="inline-flex gap-2">
                                    <button data-testid={`partner-edit-${p.id}`} onClick={() => open(p)} className="text-zinc-300 hover:text-volt p-2"><Pencil size={14} /></button>
                                    <button data-testid={`partner-delete-${p.id}`} onClick={() => del(p.id)} className="text-zinc-300 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                                </div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
